/* Nuvora — theme.js
   Sin dependencias. Cada bloque se activa solo si su HTML existe en la página. */

(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motion = root.classList.contains('motion') && !reduceMotion;
  if (!motion) root.classList.add('no-motion');

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
  const debounce = (fn, ms = 150) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };

  /* ---------- Títulos que aparecen línea por línea ---------- */

  const splitLines = (el) => {
    if (!el.dataset.splitSource) el.dataset.splitSource = el.innerHTML;
    el.innerHTML = el.dataset.splitSource;

    // Cada palabra (o elemento en línea, como <em>) se vuelve una unidad medible.
    const units = [];
    Array.from(el.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) { units.push(document.createTextNode(' ')); return; }
          const span = document.createElement('span');
          span.className = 'u';
          span.textContent = part;
          units.push(span);
        });
      } else if (node.nodeName === 'BR') {
        units.push(node.cloneNode());
      } else {
        const wrap = document.createElement('span');
        wrap.className = 'u';
        wrap.appendChild(node.cloneNode(true));
        units.push(wrap);
      }
    });
    el.textContent = '';
    units.forEach((u) => el.appendChild(u));

    const lines = [];
    let lastTop = null;
    units.forEach((u) => {
      if (u.nodeName === 'BR') { lastTop = null; return; }
      if (u.nodeType === Node.TEXT_NODE) { if (lines.length) lines[lines.length - 1].push(u); return; }
      const top = u.offsetTop;
      if (lastTop === null || Math.abs(top - lastTop) > 4) { lines.push([]); lastTop = top; }
      lines[lines.length - 1].push(u);
    });

    el.textContent = '';
    lines.forEach((words, i) => {
      const line = document.createElement('span');
      line.className = 'line';
      const inner = document.createElement('span');
      inner.style.setProperty('--l', i);
      words.forEach((w) => {
        if (w.nodeType === Node.TEXT_NODE) inner.appendChild(w);
        else while (w.firstChild) inner.appendChild(w.firstChild);
      });
      line.appendChild(inner);
      el.appendChild(line);
    });
  };

  const initSplits = () => {
    if (!motion) return;
    const els = $$('[data-split]');
    els.forEach(splitLines);
    window.addEventListener('resize', debounce(() => {
      els.forEach((el) => {
        if (el.classList.contains('is-in')) { el.innerHTML = el.dataset.splitSource; }
        else splitLines(el);
      });
    }, 200));
  };

  /* ---------- Revelado al entrar en pantalla ---------- */

  const initReveal = (ctx = document) => {
    const targets = $$('[data-reveal], [data-split], [data-inview]', ctx);
    if (!motion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
    targets.forEach((el) => io.observe(el));
  };

  /* ---------- Texto que se ilumina con el scroll ---------- */

  const initStatements = () => {
    const els = $$('[data-statement]');
    if (!els.length) return;
    els.forEach((el) => {
      const accent = (el.dataset.accent || '').toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);
      const words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach((w, i) => {
        const span = document.createElement('span');
        const clean = w.toLowerCase().replace(/[.,;:¡!¿?]/g, '');
        span.className = accent.includes(clean) ? 'w w--accent' : 'w';
        span.textContent = w;
        if (i) el.appendChild(document.createTextNode(' '));
        el.appendChild(span);
      });
    });
    if (!motion) { $$('.w', document).forEach((w) => w.classList.add('is-lit')); return; }

    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const progress = clamp((vh * 0.85 - rect.top) / (rect.height + vh * 0.35), 0, 1);
        const words = el.querySelectorAll('.w');
        const lit = Math.round(progress * words.length);
        words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  };

  /* ---------- Sección con imagen fija y pasos ---------- */

  const initSpotlights = () => {
    $$('[data-spotlight]').forEach((section) => {
      const steps = $$('.spotlight-step', section);
      const imgs = $$('.spotlight__stage img', section);
      const bars = $$('.spotlight__progress span', section);
      const setActive = (index) => {
        steps.forEach((s, i) => s.classList.toggle('is-active', i === index));
        imgs.forEach((img) => img.classList.toggle('is-active', Number(img.dataset.index) === index));
        bars.forEach((b, i) => b.classList.toggle('is-done', i <= index));
      };
      setActive(0);
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) setActive(steps.indexOf(entry.target)); });
      }, { rootMargin: '-45% 0px -45% 0px' });
      steps.forEach((s) => io.observe(s));
    });
  };

  /* ---------- Barra de anuncios ---------- */

  const initAnnouncements = () => {
    $$('[data-announcements]').forEach((bar) => {
      const items = $$('.announcement__item', bar);
      if (items.length < 2) { items[0]?.classList.add('is-active'); return; }
      let i = 0;
      items[0].classList.add('is-active');
      const speed = Number(bar.dataset.speed || 5) * 1000;
      setInterval(() => {
        if (document.hidden) return;
        const current = items[i];
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        setTimeout(() => current.classList.remove('is-leaving'), 700);
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, speed);
    });
  };

  /* ---------- Header ---------- */

  const initHeader = () => {
    const header = $('[data-header]');
    if (!header) return;
    const wrap = header.closest('.shopify-section') || header;
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      wrap.classList.toggle('is-scrolled', y > 8);
      const goingDown = y > lastY;
      const menuOpen = document.body.classList.contains('is-locked');
      wrap.classList.toggle('is-hidden', goingDown && y > 320 && !menuOpen);
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  };

  /* ---------- WhatsApp flotante: aparece al bajar ---------- */

  const initWaFloat = () => {
    const btn = $('.wa-float');
    if (!btn) return;
    const update = () => btn.classList.toggle('is-shown', window.scrollY > window.innerHeight * 0.6);
    window.addEventListener('scroll', update, { passive: true });
    update();
  };

  /* ---------- Paneles (menú y carrito) ---------- */

  const panels = {};
  let lastFocus = null;

  const openPanel = (name) => {
    const panel = panels[name];
    if (!panel) return false;
    lastFocus = document.activeElement;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    $$(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    setTimeout(() => $('[data-close], button, a', panel.querySelector('[role="dialog"]') || panel)?.focus(), 60);
    return true;
  };

  const closePanel = (name) => {
    const panel = panels[name];
    if (!panel || !panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    $$(`[aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  };

  const initPanels = () => {
    $$('[data-panel]').forEach((panel) => {
      const name = panel.dataset.panel;
      panels[name] = panel;
      panel.addEventListener('click', (e) => { if (e.target.closest('[data-close]')) closePanel(name); });
    });
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open]');
      if (!opener) return;
      if (openPanel(opener.dataset.open)) e.preventDefault();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      Object.keys(panels).forEach(closePanel);
    });
  };

  /* ---------- Carrito ---------- */

  const updateCartCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
      el.dataset.count = count;
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    });
  };

  const renderDrawer = (html) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = $('[data-drawer-content]', doc);
    const current = $('[data-drawer-content]');
    if (fresh && current) current.replaceWith(fresh);
    const count = fresh?.dataset.count;
    if (count !== undefined) updateCartCount(Number(count));
  };

  const cartRequest = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: window.location.pathname })
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'No pudimos agregar el producto. Intenta de nuevo o escríbenos por WhatsApp.');
    return data;
  };

  const addToCart = async (items, button) => {
    if (window.theme.cartType !== 'drawer' || !panels.cart) return false;
    button?.classList.add('is-loading');
    try {
      const data = await cartRequest(`${window.theme.routes.cartAdd}.js`, { items });
      renderDrawer(data.sections?.['cart-drawer']);
      openPanel('cart');
      return true;
    } catch (err) {
      const holder = button?.closest('form')?.querySelector('[data-form-error]');
      if (holder) { holder.textContent = err.message; holder.hidden = false; }
      else window.alert(err.message);
      return true;
    } finally {
      button?.classList.remove('is-loading');
    }
  };

  const initCart = () => {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-product-form]');
      if (!form || window.theme.cartType !== 'drawer' || !panels.cart) return;
      e.preventDefault();
      const fd = new FormData(form);
      const error = form.querySelector('[data-form-error]');
      if (error) error.hidden = true;
      await addToCart([{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }], form.querySelector('[type="submit"]'));
    });

    document.addEventListener('click', async (e) => {
      const quick = e.target.closest('[data-quick-add]');
      if (quick) {
        e.preventDefault();
        const handled = await addToCart([{ id: Number(quick.dataset.quickAdd), quantity: 1 }], quick);
        if (!handled) window.location.href = quick.href || window.theme.routes.cart;
        return;
      }

      const change = e.target.closest('[data-line-change]');
      if (!change) return;
      e.preventDefault();
      const line = change.closest('[data-line]');
      const key = line.dataset.line;
      const input = $('input', line);
      let qty = Number(input?.value || 0);
      const action = change.dataset.lineChange;
      if (action === 'plus') qty += 1;
      if (action === 'minus') qty = Math.max(0, qty - 1);
      if (action === 'remove') qty = 0;
      line.classList.add('is-updating');
      try {
        const data = await cartRequest(`${window.theme.routes.cartChange}.js`, { id: key, quantity: qty });
        renderDrawer(data.sections?.['cart-drawer']);
      } catch (err) {
        line.classList.remove('is-updating');
        window.alert(err.message);
      }
    });
  };

  /* ---------- Cantidad ---------- */

  const initQuantity = () => {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty]');
      if (!btn) return;
      const input = btn.parentElement.querySelector('input');
      const step = btn.dataset.qty === 'plus' ? 1 : -1;
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  /* ---------- Producto ---------- */

  const initProduct = () => {
    $$('[data-product]').forEach((section) => {
      const json = $('[data-product-json]', section);
      if (!json) return;
      const product = JSON.parse(json.textContent);
      const form = $('[data-product-form]', section);
      const idInput = form && $('input[name="id"]', form);
      const submit = form && $('[type="submit"]', form);
      const priceEls = $$('[data-price]');
      const formatter = new Intl.NumberFormat('es-CO', { style: 'currency', currency: section.dataset.currency || 'COP', maximumFractionDigits: 0 });
      const money = (cents) => formatter.format(cents / 100).replace(/\s/g, '');

      const renderPrice = (v) => {
        const save = v.compare_at_price > v.price ? Math.round((1 - v.price / v.compare_at_price) * 100) : 0;
        const html = `<span>${money(v.price)}</span>${save ? `<s class="price__compare">${money(v.compare_at_price)}</s><span class="price__save">Ahorras ${save}%</span>` : ''}`;
        priceEls.forEach((el) => { el.innerHTML = html; });
      };

      const selected = () => $$('fieldset[data-option] input:checked', section).map((i) => i.value);

      const update = () => {
        const values = selected();
        const variant = product.variants.find((v) => v.options.every((o, i) => o === values[i]));
        $$('fieldset[data-option]', section).forEach((fs) => {
          const legendValue = $('legend span', fs);
          const checked = $('input:checked', fs);
          if (legendValue && checked) legendValue.textContent = checked.value;
        });
        if (!variant) {
          if (submit) { submit.disabled = true; submit.querySelector('span').textContent = 'No disponible'; }
          return;
        }
        if (idInput) { idInput.value = variant.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
        renderPrice(variant);
        if (submit) {
          submit.disabled = !variant.available;
          submit.querySelector('span').textContent = variant.available ? submit.dataset.label : 'Agotado';
        }
        $$('[data-sticky-submit]').forEach((b) => { b.disabled = !variant.available; });
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (variant.featured_media && section.gallerySlideTo) section.gallerySlideTo(variant.featured_media.id);
      };

      section.addEventListener('change', (e) => { if (e.target.closest('fieldset[data-option]')) update(); });

      // Galería deslizable: puntos (celular), miniaturas y flechas (computador), teclado
      const track = $('.gallery__track', section);
      if (track) {
        const slides = $$('.gallery__item', track);
        const dots = $$('.gallery__dots button', section);
        const thumbs = $$('.gallery__thumb', section);
        const prev = $('[data-gallery-prev]', section);
        const next = $('[data-gallery-next]', section);
        const current = () => {
          const center = track.scrollLeft + track.clientWidth / 2;
          let idx = 0;
          slides.forEach((s, i) => { if (s.offsetLeft - track.offsetLeft <= center) idx = i; });
          return idx;
        };
        const goTo = (i) => {
          const n = Math.max(0, Math.min(slides.length - 1, i));
          const s = slides[n];
          const left = s.offsetLeft - track.offsetLeft - (track.clientWidth - s.clientWidth) / 2;
          track.scrollTo({ left, behavior: motion ? 'smooth' : 'auto' });
        };
        const sync = () => {
          const idx = current();
          dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
          thumbs.forEach((t, i) => t.classList.toggle('is-active', i === idx));
          const t = thumbs[idx];
          if (t) { // solo se desplaza la fila de miniaturas, nunca la página
            const row = t.parentElement;
            if (t.offsetLeft < row.scrollLeft) row.scrollLeft = t.offsetLeft;
            else if (t.offsetLeft + t.offsetWidth > row.scrollLeft + row.clientWidth) row.scrollLeft = t.offsetLeft + t.offsetWidth - row.clientWidth;
          }
          if (prev) prev.disabled = idx === 0;
          if (next) next.disabled = idx === slides.length - 1;
        };
        track.addEventListener('scroll', debounce(sync, 60), { passive: true });
        dots.forEach((d, i) => d.addEventListener('click', () => goTo(i)));
        thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));
        prev?.addEventListener('click', () => goTo(current() - 1));
        next?.addEventListener('click', () => goTo(current() + 1));
        track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current() + 1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current() - 1); }
        });
        section.gallerySlideTo = (mediaId) => {
          const i = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
          if (i > -1) goTo(i);
        };
        sync();
      }

      // Barra fija de compra en celular
      const sticky = $('[data-sticky-buy]');
      if (sticky && submit && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver(([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', passed);
          sticky.setAttribute('aria-hidden', String(!passed));
        });
        io.observe(form);
        $('[data-sticky-submit]', sticky)?.addEventListener('click', () => {
          if (section.hasAttribute('data-releasit-product') && !section.classList.contains('releasit-missing')) {
            const target = releasitButton();
            if (target) { target.click(); return; }
          }
          if (form.requestSubmit) form.requestSubmit(submit); else submit.click();
        });
      }
    });
  };

  /* ---------- Desplegables con diseño propio ---------- */
  /* Reemplazan visualmente a <select data-custom-select>. El select real sigue debajo
     guardando el valor, así los filtros funcionan igual y hay respaldo si falla el JS. */

  const initDropdowns = () => {
    const mobile = window.matchMedia('(max-width: 749px)');
    let openDd = null;

    const closeAll = (focusBtn) => {
      if (!openDd) return;
      const { wrap, btn, panel, scrim } = openDd;
      [wrap, panel, scrim].forEach((el) => el.classList.remove('is-open'));
      btn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('dd-sheet-open');
      if (focusBtn) btn.focus();
      openDd = null;
    };

    $$('select[data-custom-select]').forEach((select, n) => {
      const holder = select.closest('.select') || select.parentElement;
      holder.classList.add('is-enhanced');
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      const id = `dd-${n}`;
      const wrap = document.createElement('div');
      wrap.className = 'dd';
      wrap.innerHTML = `
        <button type="button" class="dd__btn" aria-haspopup="listbox" aria-expanded="false" aria-controls="${id}">
          <span class="dd__label">${select.dataset.label || ''}</span>
          <span class="dd__value"></span>
          <svg class="dd__chev" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
        </button>
        <div class="dd__panel" role="presentation">
          <p class="dd__title">${select.dataset.label || ''}</p>
          <ul class="dd__list" role="listbox" id="${id}" tabindex="-1" aria-label="${select.dataset.label || ''}"></ul>
        </div>
        <div class="dd__scrim" aria-hidden="true"></div>`;
      holder.appendChild(wrap);
      const btn = $('.dd__btn', wrap);
      const value = $('.dd__value', wrap);
      const list = $('.dd__list', wrap);
      const panel = $('.dd__panel', wrap);
      const scrim = $('.dd__scrim', wrap);
      // Fuera de la barra (que tiene desenfoque y desplazamiento lateral) para que se ubique bien
      const scheme = select.closest('.scheme-light') ? 'scheme-light' : 'scheme-dark';
      panel.classList.add(scheme);
      document.body.append(panel, scrim);

      const options = Array.from(select.options).map((opt, i) => {
        const li = document.createElement('li');
        li.id = `${id}-o${i}`;
        li.className = 'dd__opt';
        li.setAttribute('role', 'option');
        li.dataset.value = opt.value;
        li.innerHTML = `<span></span><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>`;
        li.firstChild.textContent = opt.textContent;
        list.appendChild(li);
        return li;
      });

      let active = 0;
      const setActive = (i) => {
        active = (i + options.length) % options.length;
        options.forEach((o, k) => o.classList.toggle('is-active', k === active));
        list.setAttribute('aria-activedescendant', options[active].id);
        // Desplazamos solo la lista (no la página) para mostrar la opción activa
        const o = options[active];
        if (o.offsetTop < list.scrollTop) list.scrollTop = o.offsetTop;
        else if (o.offsetTop + o.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = o.offsetTop + o.offsetHeight - list.clientHeight;
      };
      const sync = () => {
        const i = Math.max(0, select.selectedIndex);
        options.forEach((o, k) => o.setAttribute('aria-selected', String(k === i)));
        value.textContent = select.options[i]?.textContent || '';
        wrap.classList.toggle('has-value', i > 0);
      };
      const choose = (i) => {
        if (select.selectedIndex !== i) {
          select.selectedIndex = i;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        sync();
        closeAll(true);
      };
      const place = () => {
        if (mobile.matches) { panel.style.cssText = ''; return; }
        const r = btn.getBoundingClientRect();
        const width = Math.max(r.width, 220);
        const left = Math.min(r.left, window.innerWidth - width - 12);
        panel.style.cssText = `position: fixed; top: ${r.bottom + 8}px; left: ${Math.max(12, left)}px; width: ${width}px;`;
      };
      const open = (byKeyboard = false) => {
        closeAll();
        panel.classList.toggle('is-kb', byKeyboard);
        place();
        [wrap, panel, scrim].forEach((el) => el.classList.add('is-open'));
        btn.setAttribute('aria-expanded', 'true');
        if (mobile.matches) document.body.classList.add('dd-sheet-open');
        openDd = { wrap, btn, panel, scrim };
        setActive(Math.max(0, select.selectedIndex));
        list.focus({ preventScroll: true });
      };

      btn.addEventListener('click', (e) => (wrap.classList.contains('is-open') ? closeAll(true) : open(e.detail === 0)));
      btn.addEventListener('keydown', (e) => {
        if (['ArrowDown', 'ArrowUp'].includes(e.key)) { e.preventDefault(); open(true); }
      });
      list.addEventListener('keydown', (e) => {
        panel.classList.add('is-kb');
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
        else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
        else if (e.key === 'End') { e.preventDefault(); setActive(options.length - 1); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(active); }
        else if (e.key === 'Escape') { e.preventDefault(); closeAll(true); }
        else if (e.key === 'Tab') closeAll();
      });
      options.forEach((o, i) => {
        o.addEventListener('click', () => choose(i));
        o.addEventListener('mousemove', () => { if (active !== i) setActive(i); });
      });
      scrim.addEventListener('click', () => closeAll(true));
      select.addEventListener('change', sync);
      select.addEventListener('nv:sync', sync);
      sync();
    });

    document.addEventListener('click', (e) => { if (openDd && !openDd.wrap.contains(e.target) && !openDd.panel.contains(e.target)) closeAll(); });
    window.addEventListener('resize', () => closeAll());
    window.addEventListener('scroll', () => { if (openDd && !mobile.matches) closeAll(); }, { passive: true });
  };

  /* ---------- Catálogo: filtros instantáneos ---------- */

  const initCatalog = () => {
    const bar = $('[data-filters]');
    const grid = $('[data-filter-grid]');
    if (!bar || !grid) return;
    const items = $$('[data-item]', grid);
    const spaces = $$('[data-space]');
    const search = $('[data-filter-search]', bar);
    const price = $('[data-filter-price]', bar);
    const sort = $('[data-filter-sort]', bar);
    const available = $('[data-filter-available]', bar);
    const activePill = $('[data-filter-clear-group]', bar);
    const activeName = $('[data-filter-active-name]', bar);
    const count = $('[data-filter-count]');
    const empty = $('[data-filter-empty]');
    const resets = $$('[data-filter-reset]');
    const state = { group: 'all', groupName: '', q: '', price: 'all', sort: 'featured', available: false };

    const normalize = (t) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

    // Nombre del espacio (como se escribe en las ventanitas) → id del grupo
    const groupIds = {};
    $$('[data-filter-groups] li').forEach((li) => { groupIds[normalize(li.dataset.name)] = li.dataset.id; });
    const idFor = (name) => (name === '__all' ? 'all' : groupIds[normalize(name)]);
    const inGroup = (el, id) => ` ${el.dataset.groups} `.includes(` ${id} `);

    // Ocultamos las ventanitas que no tienen productos (por ejemplo "Otros" vacío)
    spaces.forEach((btn) => {
      const id = idFor(btn.dataset.space);
      if (!id) { btn.closest('li').hidden = true; return; }
      if (id !== 'all' && !items.some((el) => inGroup(el, id))) btn.closest('li').hidden = true;
    });

    const matches = (el) => {
      if (state.group !== 'all' && !inGroup(el, state.group)) return false;
      if (state.q && !normalize(el.dataset.title).includes(normalize(state.q))) return false;
      if (state.available && el.dataset.available !== 'true') return false;
      if (state.price !== 'all') {
        const [min, max] = state.price.split('-').map(Number);
        const p = Number(el.dataset.price);
        // Rangos (min, max]: el tope se incluye, así "Hasta $60.000" incluye los de $60.000.
        if ((min > 0 && p <= min) || p > max) return false;
      }
      return true;
    };

    const sorted = (list) => {
      const byIndex = (a, b) => a.dataset.index - b.dataset.index;
      const copy = [...list];
      if (state.sort === 'price-asc') return copy.sort((a, b) => a.dataset.price - b.dataset.price || byIndex(a, b));
      if (state.sort === 'price-desc') return copy.sort((a, b) => b.dataset.price - a.dataset.price || byIndex(a, b));
      if (state.sort === 'title') return copy.sort((a, b) => a.dataset.title.localeCompare(b.dataset.title, 'es'));
      return copy.sort(byIndex);
    };

    const apply = () => {
      // FLIP: guardamos posiciones antes de mover
      const first = new Map(items.filter((el) => !el.hidden).map((el) => [el, el.getBoundingClientRect()]));
      const visible = [];
      items.forEach((el) => {
        const show = matches(el);
        if (show) visible.push(el);
        el.hidden = !show;
      });
      sorted(items).forEach((el) => grid.appendChild(el));
      if (motion) {
        visible.forEach((el) => {
          const before = first.get(el);
          const after = el.getBoundingClientRect();
          if (!before) {
            el.animate([{ opacity: 0, transform: 'scale(.94)' }, { opacity: 1, transform: 'none' }], { duration: 450, easing: 'cubic-bezier(.22,1,.36,1)' });
            return;
          }
          const dx = before.left - after.left;
          const dy = before.top - after.top;
          if (dx || dy) el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 600, easing: 'cubic-bezier(.22,1,.36,1)' });
        });
      }
      spaces.forEach((btn) => {
        const on = idFor(btn.dataset.space) === state.group;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on);
      });
      if (activePill) {
        activePill.hidden = state.group === 'all';
        if (activeName) activeName.textContent = state.groupName;
      }
      if (count) count.textContent = visible.length;
      if (empty) empty.hidden = visible.length > 0;
      const active = state.group !== 'all' || state.q || state.price !== 'all' || state.available;
      resets.forEach((b) => { if (!b.closest('[data-filter-empty]')) b.hidden = !active; });
    };

    const scrollToGrid = () => {
      const body = $('.cat-body');
      const offset = bar.offsetHeight + (parseFloat(getComputedStyle(root).getPropertyValue('--header-h')) || 64);
      if (body) window.scrollTo({ top: body.getBoundingClientRect().top + window.scrollY - offset, behavior: motion ? 'smooth' : 'auto' });
    };

    // Ventanitas del encabezado: tocar una filtra; tocar la activa vuelve a "Todo"
    spaces.forEach((btn) => btn.addEventListener('click', () => {
      const id = idFor(btn.dataset.space);
      if (!id) return;
      const toggleOff = id !== 'all' && state.group === id;
      state.group = toggleOff ? 'all' : id;
      state.groupName = toggleOff ? '' : btn.querySelector('.cat-space__name')?.textContent.trim() || btn.dataset.space;
      apply();
      if (state.group !== 'all') scrollToGrid();
    }));
    activePill?.addEventListener('click', () => { state.group = 'all'; state.groupName = ''; apply(); });

    search?.addEventListener('input', debounce(() => { state.q = search.value.trim(); apply(); }, 160));
    price?.addEventListener('change', () => { state.price = price.value; apply(); });
    sort?.addEventListener('change', () => { state.sort = sort.value; apply(); });
    available?.addEventListener('change', () => { state.available = available.checked; apply(); });
    resets.forEach((b) => b.addEventListener('click', () => {
      Object.assign(state, { group: 'all', groupName: '', q: '', price: 'all', available: false });
      if (search) search.value = '';
      if (price) { price.value = 'all'; price.dispatchEvent(new Event('nv:sync')); }
      if (available) available.checked = false;
      apply();
    }));

    // Si se llega con ?espacio=Baño (desde el inicio), se abre ese espacio ya filtrado
    const wanted = new URLSearchParams(window.location.search).get('espacio');
    if (wanted) {
      const btn = spaces.find((b) => normalize(b.dataset.space) === normalize(wanted));
      if (btn) { btn.click(); }
    }

    // Tamaño de la grilla (se recuerda en este navegador)
    const densityBtns = $$('[data-density]', bar);
    const setDensity = (n) => {
      grid.dataset.cols = n;
      densityBtns.forEach((b) => b.classList.toggle('is-active', b.dataset.density === String(n)));
      try { localStorage.setItem('nuvora-cols', n); } catch (e) { /* sin almacenamiento */ }
    };
    densityBtns.forEach((b) => b.addEventListener('click', () => setDensity(b.dataset.density)));
    try { const saved = localStorage.getItem('nuvora-cols'); if (saved) setDensity(saved); } catch (e) { /* sin almacenamiento */ }
  };

  /* ---------- Modos Releasit (contra entrega) ---------- */
  /* Carrito ([data-releasit-checkout]) y producto ([data-releasit-product]): nuestros botones
     quedan ocultos por CSS. Si en 6 s no aparece ningún elemento de Releasit (app apagada o
     sin el botón activado), los volvemos a mostrar para no perder ventas. */

  // Elementos reales de Releasit: botón del producto y botón del carrito (dentro de #cart-wrapper)
  const RSI_PRODUCT = '#buttonContainerProduct #rsi_buy_now_button, #rsi_buy_now_button:not(#cart-wrapper *)';
  const RSI_CART = '#cart-wrapper #rsi_buy_now_button, #cart-wrapper button';
  const releasitButton = () => document.querySelector(RSI_PRODUCT);

  const initReleasit = () => {
    const watch = (box, selector) => {
      if (document.querySelector(selector)) return;
      const obs = new MutationObserver(() => { if (document.querySelector(selector)) { obs.disconnect(); clearTimeout(timer); } });
      obs.observe(document.body, { childList: true, subtree: true });
      const timer = setTimeout(() => {
        obs.disconnect();
        if (!document.querySelector(selector)) {
          box.classList.add('releasit-missing');
          console.warn('Nuvora: no apareció el botón de Releasit; se muestran los botones normales.');
        }
      }, 8000);
    };
    $$('[data-releasit-product]').forEach((box) => watch(box, RSI_PRODUCT));
    // En el carrito no hace falta: el pago de Shopify solo se oculta cuando existe el botón de Releasit (CSS).
  };

  /* ---------- Botón "Volver" ---------- */

  const initBack = () => {
    $$('[data-back]').forEach((link) => link.addEventListener('click', (e) => {
      // Si venimos de otra página de la tienda, regresamos a ella; si no, vamos al inicio.
      if (document.referrer && new URL(document.referrer).origin === window.location.origin && window.history.length > 1) {
        e.preventDefault();
        window.history.back();
      }
    }));
  };

  /* ---------- Carrusel de productos ---------- */

  const initCarousels = () => {
    $$('[data-carousel]').forEach((section) => {
      const track = $('[data-carousel-track]', section);
      const prev = $('[data-carousel-prev]', section);
      const next = $('[data-carousel-next]', section);
      const bar = $('[data-carousel-bar]', section);
      if (!track) return;
      const step = () => {
        const card = track.firstElementChild;
        if (!card) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const ratio = track.clientWidth / track.scrollWidth;
        const p = max > 0 ? track.scrollLeft / max : 0;
        if (bar) {
          bar.style.setProperty('--w', `${Math.max(ratio * 100, 12)}%`);
          bar.style.setProperty('--x', `${p * (100 / Math.max(ratio, .12) - 100)}%`);
        }
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
      };
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: motion ? 'smooth' : 'auto' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: motion ? 'smooth' : 'auto' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  };

  /* ---------- Productos relacionados ---------- */

  const initRecommendations = () => {
    $$('[data-recommendations]').forEach(async (holder) => {
      if (holder.children.length) return;
      try {
        const res = await fetch(holder.dataset.url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (!fresh || !fresh.children.length) { holder.remove(); return; }
        holder.innerHTML = fresh.innerHTML;
        initReveal(holder);
      } catch (e) {
        holder.remove();
      }
    });
  };

  /* ---------- Orden en colecciones ---------- */

  const initSort = () => {
    $$('[data-sort]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  };

  /* ---------- Acordeones con animación ---------- */

  const initAccordions = () => {
    if (!motion) return;
    $$('.accordion details').forEach((details) => {
      const summary = $('summary', details);
      const body = $('.accordion__body', details);
      if (!summary || !body) return;
      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (details.open) {
          const anim = body.animate([{ height: `${body.offsetHeight}px` }, { height: '0px' }], { duration: 400, easing: 'cubic-bezier(.22,1,.36,1)' });
          anim.onfinish = () => { details.open = false; };
        } else {
          details.open = true;
          const h = body.offsetHeight;
          body.animate([{ height: '0px' }, { height: `${h}px` }], { duration: 500, easing: 'cubic-bezier(.22,1,.36,1)' });
        }
      });
    });
  };

  /* ---------- Arranque ---------- */

  const start = () => {
    initPanels();
    initHeader();
    initAnnouncements();
    initCart();
    initQuantity();
    initProduct();
    initSort();
    initRecommendations();
    initAccordions();
    initSpotlights();
    initStatements();
    initCarousels();
    initDropdowns();
    initCatalog();
    initBack();
    initReleasit();
    initWaFloat();
    (document.fonts?.ready || Promise.resolve()).then(() => {
      initSplits();
      initReveal();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Editor de temas de Shopify: reactivar animaciones al editar secciones
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    initSpotlights();
  });
})();
